// Pure helpers for the version-history UI (ENG-539). No React/network here so
// they're unit-testable; the panel composes them.

export function shortId(id) {
  return String(id || '').slice(0, 7)
}

// colorForUser is a deterministic, stable per-user colour for author avatars.
export function colorForUser(userId) {
  const s = String(userId)
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return `hsl(${h % 360}, 70%, 55%)`
}

// initials derives a 1-2 char avatar label from an author id/email.
export function initials(name) {
  const s = String(name || '?').trim()
  const at = s.indexOf('@')
  const base = at > 0 ? s.slice(0, at) : s
  const parts = base.split(/[.\s_-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

const DAY_MS = 86_400_000

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

// groupCheckpointsByDate buckets checkpoints (newest-first input preserved)
// under "Today" / "Yesterday" / a formatted date, like PlayCanvas's history.
// nowMs is passed for deterministic testing.
export function groupCheckpointsByDate(checkpoints, nowMs) {
  const todayStart = startOfDay(new Date(nowMs))
  const groups = []
  const byLabel = new Map()
  for (const cp of checkpoints || []) {
    const d = new Date(cp.created_at)
    const dayStart = startOfDay(d)
    let label
    if (dayStart === todayStart) label = 'Today'
    else if (dayStart === todayStart - DAY_MS) label = 'Yesterday'
    else label = d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
    let g = byLabel.get(label)
    if (!g) {
      g = { label, items: [] }
      byLabel.set(label, g)
      groups.push(g)
    }
    g.items.push(cp)
  }
  return groups
}

// assetKindToSection maps a content-browser asset to the manifest section its
// rows live in, for per-asset Item History. Returns null for kinds whose id
// does not directly key a manifest section (prefab/gesture links, legacy slot
// textures whose rawId carries a "." suffix) — callers hide the
// Item-History action for those rather than show an always-empty history.
export function assetKindToSection(asset) {
  switch (asset?.kind) {
    case 'material': return 'material_assets'
    case 'texture': return String(asset.rawId || '').includes('.') ? null : 'texture_assets'
    case 'body_anim':
    case 'face_anim': return 'animation_assets'
    case 'audio': return 'audio_assets'
    case 'environment': return 'environment_assets'
    default: return null
  }
}

// layoutCheckpointGraph maps checkpoints + branches to a simple lane/row grid
// for the Graph view (PlayCanvas-style commit graph). Input checkpoints are
// newest-first (as the API returns them); row 0 is the newest. Each branch gets
// its own lane (column); checkpoints with no branch fall into a trailing lane.
// Edges connect a checkpoint to its parent when the parent is in the set.
// Pure — no DOM — so the SVG renderer and tests share one source of truth.
export function layoutCheckpointGraph(checkpoints, branches) {
  const cps = Array.isArray(checkpoints) ? checkpoints : []
  const laneOf = new Map()
  for (const b of branches || []) {
    if (!laneOf.has(b.id)) laneOf.set(b.id, laneOf.size)
  }
  const noBranchLane = laneOf.size // trailing lane for branchless checkpoints
  const idToRow = new Map()
  cps.forEach((cp, i) => idToRow.set(cp.id, i))

  let usedNoBranchLane = false
  const nodes = cps.map((cp, row) => {
    let lane
    if (cp.branch_id != null && laneOf.has(cp.branch_id)) {
      lane = laneOf.get(cp.branch_id)
    } else {
      lane = noBranchLane
      usedNoBranchLane = true
    }
    return { ...cp, row, lane }
  })
  const edges = []
  for (const cp of cps) {
    if (cp.parent_id != null && idToRow.has(cp.parent_id)) {
      edges.push({ from: cp.parent_id, to: cp.id })
    }
  }
  const laneCount = Math.max(laneOf.size + (usedNoBranchLane ? 1 : 0), 1)
  return { nodes, edges, laneCount }
}

// summarizeChanges counts a diff's changes by type for a compact summary line.
export function summarizeChanges(changes) {
  const out = { added: 0, removed: 0, modified: 0 }
  for (const c of changes || []) {
    if (out[c.type] != null) out[c.type] += 1
  }
  return out
}
